import { router } from '@forge/bridge'
import { Tooltip } from '@heroui/react'
import { useEffect, useState } from 'react'
import { logError } from '@/components/Error/Error'
import type {
  BillingInterval,
  LicenseStatus,
  SubscriptionTier
} from '../../../../types'
import { ErrorDisplay } from '../../components/Error/Error'
import { Loading } from '../../components/Loading/Loading'
import { api } from '../../lib/api'
import styles from './AdminBillingPage.module.css'

const BillingContent: React.FC = () => {
  const [processingPayment, setProcessingPayment] = useState(false)
  const [interval, setInterval] = useState<BillingInterval>('month')
  const [licenseStatus, setLicenseStatus] = useState<LicenseStatus | null>(null)
  const [isLoadingStatus, setIsLoadingStatus] = useState(true)

  useEffect(() => {
    const checkLicenseStatus = async () => {
      try {
        const status = await api.checkSubscriptionStatus()
        setLicenseStatus(status)
      } catch (error) {
        logError('AdminBillingPage', 'Error checking license status:', error)
      } finally {
        setIsLoadingStatus(false)
      }
    }
    checkLicenseStatus()
  }, [])

  const handleSubscribe = async (tier: SubscriptionTier) => {
    try {
      setProcessingPayment(true)

      const { url } = await api.createCheckoutSession(tier, interval)
      console.log('url:', url)
      // Use Forge router to open the checkout URL in a new window
      router.open(url)
    } catch (error) {
      logError('AdminBillingPage', 'Subscription error:', error)
    } finally {
      setProcessingPayment(false)
    }
  }

  const handleManageSubscription = async () => {
    try {
      setProcessingPayment(true)
      const { url } = await api.createCustomerPortalSession()
      router.open(url)
    } catch (error) {
      logError('AdminBillingPage', 'Portal session error:', error)
    } finally {
      setProcessingPayment(false)
    }
  }

  const renderSubscribeButton = (tier: SubscriptionTier) => {
    // Don't render anything while loading the license status
    if (isLoadingStatus) {
      return null
    }

    if (licenseStatus?.isActive) {
      // Only show Manage Subscription button for the active plan
      if (licenseStatus.tier === tier) {
        return (
          <button
            type="button"
            className={`${styles.subscribeButton} ${processingPayment ? styles.loading : ''}`}
            onClick={handleManageSubscription}
            disabled={processingPayment}
          >
            {processingPayment ? 'Processing...' : 'Manage Subscription'}
          </button>
        )
      }
      // Don't show any button for non-active plans when there's an active subscription
      return null
    }

    // No active subscription, show Subscribe for all tiers
    return (
      <button
        type="button"
        className={`${styles.subscribeButton} ${processingPayment ? styles.loading : ''}`}
        onClick={() => handleSubscribe(tier)}
        disabled={processingPayment}
      >
        {processingPayment ? 'Processing...' : 'Subscribe'}
      </button>
    )
  }

  return (
    <div className={styles.pricingContainer} data-testid="pricing-page">
      {/* Interval Toggle */}
      <div className={styles.intervalToggle} data-testid="interval-toggle">
        <button
          type="button"
          className={`${styles.toggleButton} ${interval === 'month' ? styles.active : ''}`}
          onClick={() => setInterval('month')}
          aria-pressed={interval === 'month'}
          data-testid="monthly-toggle"
        >
          Monthly
        </button>
        <button
          type="button"
          className={`${styles.toggleButton} ${interval === 'year' ? styles.active : ''}`}
          onClick={() => setInterval('year')}
          aria-pressed={interval === 'year'}
          data-testid="yearly-toggle"
        >
          Yearly
        </button>
      </div>

      {/* Pricing Cards */}
      <div className={styles.pricingGrid} data-testid="pricing-grid">
        {/* Basic Plan */}
        <div
          className={`${styles.pricingCard} ${licenseStatus?.tier === 'BASIC' ? styles.activePlan : ''}`}
          data-testid="basic-plan-card"
        >
          <div className={styles.cardHeader}>
            <h2 id="basic-plan-title">Tech Radar Basic</h2>
            {licenseStatus?.tier === 'BASIC' ? (
              <span
                className={styles.activeBadge}
                data-testid="basic-plan-badge"
              >
                Current Plan
              </span>
            ) : (
              <span
                className={styles.popularBadge}
                data-testid="basic-plan-badge"
              >
                Most popular
              </span>
            )}
          </div>
          <div className={styles.price}>
            <span className={styles.amount}>
              ${interval === 'month' ? '49' : '499'}
            </span>
            <span className={styles.interval}>/{interval}</span>
          </div>
          <ul
            className={styles.featureList}
            data-testid="basic-plan-features"
            aria-label="Basic plan features"
          >
            <li>Unlimited Radars</li>
            <li>Unlimited Radar Items</li>
            <li>Unlimited Users</li>
            <li>Full Customization</li>
            <li>Data Import & Export</li>
            <li>Email Support</li>
          </ul>
          {renderSubscribeButton('BASIC')}
        </div>

        {/* Pro Plan */}
        <div
          className={`${styles.pricingCard} ${styles.proPlan} ${licenseStatus?.tier === 'PRO' ? styles.activePlan : ''}`}
          data-testid="pro-plan-card"
        >
          <div className={styles.cardHeader}>
            <h2 id="pro-plan-title">Tech Radar Pro</h2>
            {licenseStatus?.tier === 'PRO' ? (
              <span className={styles.activeBadge} data-testid="pro-plan-badge">
                Current Plan
              </span>
            ) : (
              <span
                className={styles.comingSoonBadge}
                data-testid="pro-plan-badge"
              >
                Coming Soon
              </span>
            )}
          </div>
          <div className={styles.price}>
            <span className={styles.amount}>
              ${interval === 'month' ? '199' : '1,999'}
            </span>
            <span className={styles.interval}>/{interval}</span>
          </div>
          <ul
            className={styles.featureList}
            data-testid="pro-plan-features"
            aria-label="Pro plan features"
          >
            <li>Unlimited Radars</li>
            <li>Unlimited Radar Items</li>
            <li>Unlimited Users</li>
            <li>Full Customization</li>
            <li>Data Import & Export</li>
            <li>Email Support</li>
            <li>
              <Tooltip
                content="Track Compass components on the tech radar"
                placement="top"
                size="sm"
                delay={0}
                closeDelay={200}
              >
                <span
                  className={styles.highlightedFeature}
                  role="tooltip"
                  aria-label="Compass Component Radar Items - Track Compass components on the tech radar"
                >
                  Compass Component Radar Items
                </span>
              </Tooltip>
            </li>
            <li>
              <Tooltip
                content="Automatically updates versioned Confluence pages as a backup for tech radar changes"
                placement="top"
                size="sm"
                delay={0}
                closeDelay={200}
              >
                <span
                  role="tooltip"
                  aria-label="Confluence Data Sync - Automatically updates versioned Confluence pages as a backup for tech radar changes"
                >
                  Confluence Data Sync
                </span>
              </Tooltip>
            </li>
          </ul>
          {renderSubscribeButton('PRO')}
        </div>
      </div>

      {/* Free Tier - Horizontal Card */}
      <div
        className={`${styles.freeTierCard} ${!licenseStatus?.isActive ? styles.activePlan : ''}`}
      >
        <div className={styles.freeTierContent}>
          <div className={styles.freeTierHeader}>
            <h2>Free Tier</h2>
            {!licenseStatus?.isActive && (
              <span className={styles.activeBadge}>Current Plan</span>
            )}
          </div>
          <div className={styles.freeTierDescription}>
            <p>Perfect for getting started with a tech radar.</p>
            <ul className={styles.freeTierFeatures}>
              <li>10 Items per Radar</li>
              <li>1 Radar Limit</li>
              <li>Unlimited Users</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

const BillingPage: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const initApp = async () => {
      try {
        await api.getEnv()
      } catch (error) {
        logError('AdminBillingPage', 'Failed to initialize:', error)
        setError(error as Error)
      } finally {
        setLoading(false)
      }
    }
    initApp()
  }, [])

  if (loading) return <Loading />
  if (error) return <ErrorDisplay error={error} />

  return <BillingContent />
}

export default BillingPage
